import React, { useState, useEffect, useCallback } from "react"
import { CheckCircle2, Circle, AlertCircle, ClipboardCheck } from "lucide-react"
import { Progress } from "src/components/ui/progress"
import { fetchBookingChecklist } from "../api/documentsApi"

const ROW_ICON = {
  present: { Icon: CheckCircle2, className: "text-emerald-500" },
  expired: { Icon: AlertCircle, className: "text-red-500" },
  missing: { Icon: Circle, className: "text-slate-300" },
}

const ROW_HINT = {
  present: null,
  expired: "Expired — re-upload",
  missing: "Not uploaded",
}

// Per-stage document compliance for a booking. `refreshKey` lets the parent
// force a reload after a document is uploaded/removed.
export function BookingChecklist({ bookingId, refreshKey }) {
  const [checklist, setChecklist] = useState(null)

  const load = useCallback(() => {
    fetchBookingChecklist(bookingId).then(setChecklist).catch(() => setChecklist(null))
  }, [bookingId])

  useEffect(() => { load() }, [load, refreshKey])

  if (!checklist || checklist.required === 0) return null

  const pct = Math.round((checklist.satisfied / checklist.required) * 100)

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <ClipboardCheck className="h-4 w-4 text-slate-400" /> Document checklist
        </h3>
        <span className={`text-xs font-medium ${checklist.complete ? "text-emerald-600" : "text-slate-500"}`}>
          {checklist.satisfied} of {checklist.required} complete
        </span>
      </div>

      <Progress value={pct} className="mb-4 h-1.5" />

      <ul className="space-y-2.5">
        {checklist.items.map((item) => {
          const { Icon, className } = ROW_ICON[item.status] || ROW_ICON.missing
          const hint = ROW_HINT[item.status]
          return (
            <li key={item.category} className="flex items-center gap-2.5 text-sm">
              <Icon className={`h-4 w-4 shrink-0 ${className}`} />
              <span className={item.status === "present" ? "text-slate-700" : "text-slate-500"}>{item.label}</span>
              {hint && <span className="ml-auto text-[11px] font-medium text-slate-400">{hint}</span>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
